import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { updateWorkerProfile } from '../api';
import detailMessage from '../detailMessage';
import { useLang } from '../LangContext';

interface ProfileFormData {
  first_name: string;
  last_name: string;
  e_mail: string;
}

type FieldErrors = Partial<Record<keyof ProfileFormData, string>>;

interface Props {
  token: string;
  initialFirstName?: string;
  initialLastName?: string;
  initialEmail?: string;
  initialPendingEmail?: string;
}

const ProfileForm = ({ token, initialFirstName, initialLastName, initialEmail, initialPendingEmail }: Props) => {
  const { t } = useLang();

  const validate = (formData: ProfileFormData): FieldErrors => {
    const errors: FieldErrors = {};
    if (!formData.first_name.trim()) errors.first_name = t.firstNameRequired;
    else if (formData.first_name.trim().length < 2) errors.first_name = t.firstNameTooShort;
    if (!formData.last_name.trim()) errors.last_name = t.lastNameRequired;
    else if (formData.last_name.trim().length < 2) errors.last_name = t.lastNameTooShort;
    if (!formData.e_mail.trim()) errors.e_mail = t.emailRequired;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.e_mail)) errors.e_mail = t.invalidEmail;
    return errors;
  };

  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: initialFirstName || '',
    last_name: initialLastName || '',
    e_mail: initialEmail || '',
  });
  const [pendingEmail, setPendingEmail] = useState<string>(initialPendingEmail || '');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    setFormData({
      first_name: initialFirstName || '',
      last_name: initialLastName || '',
      e_mail: initialEmail || '',
    });
    setPendingEmail(initialPendingEmail || '');
  }, [initialFirstName, initialLastName, initialEmail, initialPendingEmail]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name as keyof ProfileFormData]) setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setSuccess('');

    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response: any = await updateWorkerProfile(formData, token);
      setSuccess(response.message || 'Profile updated successfully');
      setFormData((prev) => ({
        ...prev,
        first_name: response.first_name ?? prev.first_name,
        last_name: response.last_name ?? prev.last_name,
        e_mail: response.e_mail ?? prev.e_mail,
      }));
      setPendingEmail(response.pending_email || '');
      setTimeout(() => setSuccess(''), 8000);
    } catch (err) {
      console.error('Profile update error:', err);
      setError(detailMessage(err, t.profileUpdateFailed));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="profile-section">
      {error && <div className="error-message"><strong>{t.errorLabel2}</strong> {error}</div>}
      {success && <div className="success-message"><strong>✓</strong> {success}</div>}

      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-group">
          <label htmlFor="first_name">{t.firstNameLabel}</label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            value={formData.first_name}
            onChange={handleInputChange}
            className={fieldErrors.first_name ? 'error' : ''}
            disabled={isSubmitting}
          />
          {fieldErrors.first_name && <span className="field-error">{fieldErrors.first_name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="last_name">{t.lastNameLabel}</label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            value={formData.last_name}
            onChange={handleInputChange}
            className={fieldErrors.last_name ? 'error' : ''}
            disabled={isSubmitting}
          />
          {fieldErrors.last_name && <span className="field-error">{fieldErrors.last_name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="e_mail">{t.emailAddressLabel}</label>
          <input
            type="email"
            id="e_mail"
            name="e_mail"
            value={formData.e_mail}
            onChange={handleInputChange}
            className={fieldErrors.e_mail ? 'error' : ''}
            disabled={isSubmitting}
          />
          {fieldErrors.e_mail && <span className="field-error">{fieldErrors.e_mail}</span>}
          {pendingEmail && pendingEmail !== formData.e_mail && (
            <span className="field-hint">
              {t.pendingEmailHint(pendingEmail)}
            </span>
          )}
        </div>

        <button type="submit" className="submit-button" disabled={isSubmitting}>
          {isSubmitting ? t.updatingButton : t.updateProfileButton}
        </button>
      </form>
    </div>
  );
};

export default ProfileForm;
